"use client";

import { CSSProperties, ReactNode, useRef } from "react";
import { Move } from "lucide-react";

import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { LomdaV3Block, TextBackground } from "@/lib/utils/lomdaV3/types";

import "./sectionBand.css";

/** The section background a block carries, if its type supports one. */
export const blockBackground = (
    block: LomdaV3Block
): TextBackground | undefined =>
    block.type === "text" ? block.background : undefined;

/** A background only exists once it has something to paint. */
export const hasSectionBackground = (bg: TextBackground | undefined): boolean =>
    !!(bg?.color || bg?.imageUrl);

/** The image block's fill modes, as a CSS background-size. "fill" stretches,
 *  so it is the only one that can distort — same trade-off the image block has. */
const bandFillSize = (fill: TextBackground["fill"]): string => {
    switch (fill) {
        case "fit":
        case "contain":
            return "contain";
        case "fill":
            return "100% 100%";
        default:
            return "cover";
    }
};

/** Public URLs can carry quotes/parens that would break `url(...)`. */
const cssUrl = (url: string) => `url("${url.replace(/"/g, "%22")}")`;

/**
 * A band = the block's background stretched to the full width of the PAGE,
 * while its text stays in the centred column.
 *
 * The width comes from container-query units, not `100vw`: the page is a size
 * container (canvas.tsx / previewPlayer.tsx), so `100cqw` is the canvas in the
 * editor, the screen in the preview, and the frame inside an LMS iframe —
 * `100vw` would be the browser window in all three and overflow the editor.
 * `margin-inline-start` (not `margin-left`) keeps it correct in Hebrew.
 */
export function bandStyle(bg: TextBackground): CSSProperties {
    const bleed: CSSProperties =
        bg.fullWidth === false
            ? {}
            : { width: "100cqw", marginInlineStart: "calc(50% - 50cqw)" };

    // Layers, topmost first: the readability overlay, then the photo, then the
    // colour. Each needs its OWN size/position — a gradient sized "contain"
    // (the photo's Fit mode) would stop short of the band's edges.
    const layers: { image: string; size: string; position: string }[] = [];
    const overlay = bg.overlay ?? 0;
    if (bg.imageUrl && overlay > 0) {
        layers.push({
            image: `linear-gradient(rgba(0,0,0,${overlay}), rgba(0,0,0,${overlay}))`,
            size: "100% 100%",
            position: "center",
        });
    }
    if (bg.imageUrl) {
        layers.push({
            image: cssUrl(bg.imageUrl),
            size: bandFillSize(bg.fill),
            position: `${bg.focalX ?? 50}% ${bg.focalY ?? 50}%`,
        });
    }
    if (bg.color && bg.colorTo) {
        layers.push({
            image: `linear-gradient(${bg.gradientAngle ?? 180}deg, ${bg.color}, ${bg.colorTo})`,
            size: "100% 100%",
            position: "center",
        });
    }

    return {
        ...bleed,
        // A MINIMUM in both cases: the band can still grow with its text.
        ...(bg.fullHeight
            ? { minHeight: "100cqh" }
            : bg.minHeight
              ? { minHeight: bg.minHeight }
              : {}),
        // Stays under the layers: it shows through a "Fit" photo's letterbox.
        ...(bg.color ? { backgroundColor: bg.color } : {}),
        ...(layers.length
            ? {
                  backgroundImage: layers.map((l) => l.image).join(", "),
                  backgroundSize: layers.map((l) => l.size).join(", "),
                  backgroundPosition: layers
                      .map((l) => l.position)
                      .join(", "),
                  backgroundRepeat: "no-repeat",
              }
            : {}),
    };
}

/**
 * Re-centres the content inside a band. Mirrors the page's own column
 * (canvas.tsx / previewPlayer.tsx) so a section with a background lines its
 * text up with every section without one.
 */
export function BandColumn({
    bleeding,
    children,
}: {
    bleeding: boolean;
    children: ReactNode;
}) {
    if (!bleeding) return <>{children}</>;
    return (
        <div className="mx-auto w-full max-w-[1024px] px-[var(--lomda-page-gutter,20px)]">
            {children}
        </div>
    );
}


/** Free placement needs somewhere to place INTO: an auto band is exactly as
 *  tall as its text, so there is no room to move within. */
export const bandHasRoom = (bg: TextBackground | undefined): boolean =>
    !!(bg?.fullHeight || bg?.minHeight);

/** Whether the author has actually placed the text (vs. leaving it in flow). */
export const textIsPlaced = (bg: TextBackground | undefined): boolean =>
    bandHasRoom(bg) && bg?.textX !== undefined && bg?.textY !== undefined;

/**
 * The text, placed on the band by the author. Rich text owns click-and-drag
 * inside itself (that is how you select words), so moving is done from a handle
 * on the box's edge rather than from the text body.
 */
export function BandText({
    background,
    width,
    editable,
    onMove,
    children,
}: {
    background: TextBackground;
    /** The block's own width setting, as a percentage. */
    width: number;
    editable: boolean;
    onMove: (x: number, y: number) => void;
    children: ReactNode;
}) {
    const boxRef = useRef<HTMLDivElement>(null);
    // Where inside the box the pointer grabbed it, so the box doesn't jump its
    // top-start corner to the cursor on the first move.
    const grab = useRef({ x: 0, y: 0 });

    const placed = textIsPlaced(background);
    // Placed: percentages the stylesheet turns into a position (desktop only).
    // Not placed: the block's own width, centred — exactly what it was before a
    // background was added.
    const style = placed
        ? ({
              "--lomda-text-x": `${background.textX}%`,
              "--lomda-text-y": `${background.textY}%`,
              "--lomda-text-w": `${width}%`,
          } as CSSProperties)
        : ({ maxWidth: `${width}%` } as CSSProperties);

    const moveTo = (clientX: number, clientY: number) => {
        const box = boxRef.current;
        const band = box?.offsetParent as HTMLElement | null;
        if (!box || !band) return;
        const bandRect = band.getBoundingClientRect();
        const x = clientX - bandRect.left - grab.current.x;
        const y = clientY - bandRect.top - grab.current.y;
        // Clamp by the box's own size so it can be pushed to an edge but never
        // out of the band.
        const maxX = Math.max(0, bandRect.width - box.offsetWidth);
        const maxY = Math.max(0, bandRect.height - box.offsetHeight);
        onMove(
            Math.round((Math.min(Math.max(x, 0), maxX) / bandRect.width) * 100),
            Math.round((Math.min(Math.max(y, 0), maxY) / bandRect.height) * 100)
        );
    };

    return (
        <div
            ref={boxRef}
            className={cn(
                "w-full",
                placed ? "lomda-band-text" : "mx-auto",
                // Only while it sits in normal flow: a PLACED box is already
                // positioned (absolute, from the stylesheet), and adding
                // `relative` to the same element is a coin toss on which rule
                // the bundler emits last — it could quietly cancel the
                // placement. The move handle anchors to the box either way.
                !placed && editable && "relative"
            )}
            style={style}
        >
            {editable && bandHasRoom(background) && (
                <button
                    type="button"
                    aria-label={t("lomda-v3-editor.text.background-move")}
                    title={t("lomda-v3-editor.text.background-move")}
                    onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const box = boxRef.current;
                        if (!box) return;
                        const rect = box.getBoundingClientRect();
                        // A box still in normal flow has no offset yet; grabbing
                        // from its current spot makes the first drag continuous.
                        grab.current = {
                            x: e.clientX - rect.left,
                            y: e.clientY - rect.top,
                        };
                        e.currentTarget.setPointerCapture(e.pointerId);
                        moveTo(e.clientX, e.clientY);
                    }}
                    onPointerMove={(e) => {
                        if (!e.currentTarget.hasPointerCapture(e.pointerId))
                            return;
                        moveTo(e.clientX, e.clientY);
                    }}
                    onPointerUp={(e) =>
                        e.currentTarget.releasePointerCapture(e.pointerId)
                    }
                    // Beside the text, not on top of it — the box's own edge is
                    // where the words start. `start` (not left) so it moves to
                    // the other side in Hebrew.
                    className="absolute -start-9 top-1/2 z-30 hidden size-6 -translate-y-1/2 cursor-move items-center justify-center rounded-md border border-border bg-card text-muted-foreground shadow-sm transition hover:text-[hsl(var(--lomda-card-text))] group-hover/block:flex"
                >
                    <Move className="size-3.5" />
                </button>
            )}
            {children}
        </div>
    );
}
